import * as THREE from 'three';
import { gsap } from "gsap";

export function initTopVisual() {
  // 中心が0,0
  // ワールド座標がウィンドウサイズに合うようカメラを調整している
  let isInitialized = false;

  // 色をRGB形式の文字列で定義します。
  // 明暗の差が大/小/中/小としている
  const topRectangleColors = ["353535", "1E1E1E", "2D2D2D", "212121"];
  const rightRectangleColors = ["232323", "252525", "212121", "272727"];
  const bottomPentagonColors = ["000000", "111111", "191919", "121212"];
  const leftRectangleColors = ["111111", "171717", "252525", "2C2C2C"];
  const textColors = ["ababab", "7b7b7b", "9b9b9b", "7b7b7b"];
  // const textColors = ["9b9b9b", "5b5b5b", "7b7b7b", "5b5b5b"];

  // 現在の色のインデックスを保持する変数
  let currentColorIndex = 0;

  const targetElement = document.getElementById("top-visual");
  const titleImageElement = document.getElementById("title-image");

  // ウィンドウのサイズに基づいて四角形のサイズを計算
  // let windowWidth = window.innerWidth;
  // let windowHeight = window.innerHeight;
  let windowWidth = targetElement.clientWidth;
  let windowHeight = targetElement.clientHeight;

  // シーンとカメラのセットアップ
  const scene = new THREE.Scene();
  const fov = 75;
  const camera = new THREE.PerspectiveCamera(75, windowWidth / windowHeight, 0.01, 1000);
  let z = windowHeight / Math.tan(fov * Math.PI / 360) * 0.5;
  camera.position.set(0, 0, z);
  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setSize(windowWidth, windowHeight);
  targetElement.appendChild(renderer.domElement);

  let topRectangle, bottomPentagon, leftRectangle, rightRectangle;

  // サイズとポジションを指定して四角形を作成する関数
  function createRectangle(width, height, color, x, y) {
    const geometry = new THREE.PlaneGeometry(width, height);
    const material = new THREE.MeshBasicMaterial({ color: color, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, y, 0);
    scene.add(mesh);
    return mesh;
  }

  // 独立した四角形を作成し、指定された位置に配置する関数
  function createFixedRectangle(width, height, color, x, y) {
    // BufferGeometryを使用して四角形を作成
    const geometry = new THREE.BufferGeometry();
    // 頂点の位置を定義
    const vertices = new Float32Array([
      -width / 2, -height / 2, 0,  // 左下
      width / 2, -height / 2, 0,  // 右下
      width / 2,  height / 2, 0,  // 右上
      -width / 2,  height / 2, 0   // 左上
    ]);
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setIndex([0, 2, 3, 0, 1, 2]); // 2つの三角形で四角形を形成

    const material = new THREE.MeshBasicMaterial({ color: color, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, y, 0);
    scene.add(mesh);
    return mesh;
  }

  // 五角形を作成する関数
  function createBottomPentagon(width, height, color, x, y) {
    const shape = new THREE.Shape();

    // 五角形の底辺の中点を起点とする
    shape.moveTo(0, -height / 2);
    // 底辺の左端へ線を引く
    shape.lineTo(-width / 2, -height / 2);
    // 左上の角へ線を引く
    shape.lineTo(-width / 2, 0);
    // 上端の中点（五角形の頂点）へ線を引く
    shape.lineTo(0, -height / 8);
    // 右上の角へ線を引く
    shape.lineTo(width / 2, 0);
    // 底辺の右端へ線を引く
    shape.lineTo(width / 2, -height / 2);
    // 底辺の中点に戻る
    shape.lineTo(0, -height / 2);

    const geometry = new THREE.ShapeGeometry(shape);
    const material = new THREE.MeshBasicMaterial({ color: color, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, y, 0);
    scene.add(mesh);
    return mesh;
  }

  function createScene() {
    // シーンを作成するコード...
    // シーンに追加する前に各メッシュをグローバル変数に代入する
    topRectangle = createRectangle(
        windowWidth,
        windowHeight,
        parseInt(topRectangleColors[currentColorIndex], 16),
        0,
        0,
    );
    bottomPentagon = createBottomPentagon(
        windowWidth,
        windowHeight,
        parseInt(bottomPentagonColors[currentColorIndex], 16),
        0,
        -windowHeight / 8,
    );
    leftRectangle = createFixedRectangle(
        windowWidth / 4, windowHeight,
        parseInt(leftRectangleColors[currentColorIndex], 16),
        -windowWidth / 2 + windowWidth / 8,
        0,
    );
    rightRectangle = createFixedRectangle(
        windowWidth / 4,
        windowHeight,
        parseInt(rightRectangleColors[currentColorIndex], 16),
        windowWidth / 2 - windowWidth / 8,
        0
    );
  }

  // 頂点のアニメーションにランダムなオフセットを追加するための関数
  function randomOffset() {
    return Math.random() * 2 - 1; // -1から1の範囲でランダムな値を生成
  }

  // 五角形の中央上部の頂点にアニメーションを適用する関数
  function animatePentagonVertex(mesh, vertexIndex) {
    const positions = mesh.geometry.attributes.position.array;

    // 中央上部の頂点は、頂点リストの3番目の頂点です (0, height / 8, 0) に対応
    // 頂点の位置は、vertices配列の8, 9, 10番目に格納されています。
    const i = vertexIndex * 3;

    const time = performance.now() * 0.0003;

    // Y座標にアニメーションを適用
    positions[i + 1] = Math.cos(time * 0.6) * windowHeight / 4;
    // positions[i + 1] = Math.cos(time * 0.6) * windowHeight / 8
    //     + Math.cos(time * 0.8) * windowHeight / 8;

    // Three.jsに頂点の位置が更新されたことを通知
    mesh.geometry.attributes.position.needsUpdate = true;
  }

  // // 左側の四角形の特定の頂点にアニメーションを適用する関数
  // function animateLeftRectangleVertex(mesh, vertexIndex) {
  //   const positions = mesh.geometry.attributes.position.array;
  //   const i = vertexIndex * 3; // 頂点インデックスを配列インデックスに変換
  //   // Y座標にsin関数を適用してふわふわとした動きを作成
  //   positions[i + 1] = windowHeight / 2 * 0.5 + Math.sin(performance.now() * 0.0004) * windowHeight / 8;
  //   // X座標にもアニメーションを適用
  //   positions[i] += Math.sin(performance.now() * 0.0003) * 0.2; // X軸の動きを加える
  //   // Three.jsに頂点の位置が更新されたことを通知
  //   mesh.geometry.attributes.position.needsUpdate = true;
  // }
  //
  // // 右側の四角形の特定の頂点にアニメーションを適用する関数
  // function animateRightRectangleVertex(mesh, vertexIndex) {
  //   const positions = mesh.geometry.attributes.position.array;
  //   const i = vertexIndex * 3; // 頂点インデックスを配列インデックスに変換
  //   // Y座標にcos関数を適用してふわふわとした動きを作成
  //   positions[i + 1] = windowHeight / 2 * 0.5 + Math.cos(performance.now() * 0.00035) * windowHeight / 8;
  //   // X座標にもアニメーションを適用
  //   positions[i] += Math.cos(performance.now() * 0.00045) * 0.3; // X軸の動きを加える
  //   // Three.jsに頂点の位置が更新されたことを通知
  //   mesh.geometry.attributes.position.needsUpdate = true;
  // }

  // 左側の四角形の特定の頂点に複雑な周期的な動きを適用する関数
  function animateLeftRectangleVertex(mesh, vertexIndex) {
    const positions = mesh.geometry.attributes.position.array;
    const i = vertexIndex * 3; // 頂点インデックスを配列インデックスに変換
    const initialYPosition = windowHeight / 2 * 0.5; // Y軸の初期位置

    // 現在の時間に基づく角度
    const time = performance.now() * 0.0003;

    // X軸とY軸に対する複雑な動き
    positions[i] += Math.sin(time * 1.2) * windowWidth * 0.0004 - Math.sin(time * 0.7) * windowWidth * 0.0002; // X軸の動き
    positions[i + 1] = initialYPosition + Math.cos(time * 1.5) * windowHeight / 4 + Math.cos(time * 0.4) * windowHeight / 8; // Y軸の動き

    // Three.jsに頂点の位置が更新されたことを通知
    mesh.geometry.attributes.position.needsUpdate = true;
  }

  // 右側の四角形の特定の頂点に複雑な周期的な動きを適用する関数
  function animateRightRectangleVertex(mesh, vertexIndex) {
    const positions = mesh.geometry.attributes.position.array;
    const i = vertexIndex * 3; // 頂点インデックスを配列インデックスに変換
    const initialYPosition = windowHeight / 2 * 0.5; // Y軸の初期位置

    // 現在の時間に基づく角度
    const time = performance.now() * 0.00035;

    // X軸とY軸に対する複雑な動き
    positions[i] += Math.cos(time * 1.3) * windowWidth * 0.0002 + Math.cos(time * 0.6) * windowWidth * 0.0001; // X軸の動き
    positions[i + 1] = initialYPosition + Math.sin(time * 1.4) * windowHeight / 8 + Math.sin(time * 0.5) * windowHeight / 16; // Y軸の動き

    // Three.jsに頂点の位置が更新されたことを通知
    mesh.geometry.attributes.position.needsUpdate = true;
  }

  // ウィンドウのリサイズに対応する関数
  function onWindowResize() {
    windowWidth = targetElement.clientWidth;
    windowHeight = targetElement.clientHeight;
    // windowWidth = window.innerWidth;
    // windowHeight = window.innerHeight;

    // カメラのアスペクト比を更新
    camera.aspect = windowWidth / windowHeight;
    camera.position.z = windowHeight
      / Math.tan(fov * Math.PI / 360) * 0.5;
    // カメラの投影行列を更新
    camera.updateProjectionMatrix();
    // レンダラーのサイズを更新
    renderer.setSize(windowWidth, windowHeight);

    // シーンから既存のメッシュを削除
    scene.remove(topRectangle);
    scene.remove(bottomPentagon);
    scene.remove(leftRectangle);
    scene.remove(rightRectangle);

    // 新しいウィンドウサイズに基づいてメッシュを再作成
    createScene();
  }

  // シーンの初期作成
  createScene();

  // ウィンドウのリサイズイベントにハンドラーを追加
  window.addEventListener('resize', onWindowResize, false);

  // GSAPのto()メソッドを使用して、カラーオブジェクトのr、g、bプロパティをアニメーションさせます。
  function animateColorChange(mesh, colorArray) {
    let colorIndex = 0; // 現在の色のインデックス

    function updateColor() {
      const nextColorIndex = currentColorIndex;
      const nextHexColor = colorArray[nextColorIndex];
      // 16進数の色をRGB値に変換
      const nextColor = new THREE.Color("#" + nextHexColor);

      // GSAPを使って色を変化させる
      gsap.to(mesh.material.color, {
        r: nextColor.r,
        g: nextColor.g,
        b: nextColor.b,
        duration: 1.6,
        ease: "power1.out",

        // アニメーション完了後に次の色の変化をスケジュール
        onComplete: () => {
          // setTimeout(updateColor, 5000);
        }
      });

      // 次の色のインデックスを更新
      colorIndex = nextColorIndex;
    }

    // 最初の色の変化を開始
    updateColor();
  }

  // テキストカラー変更
  function hex2rgb ( hex ) {
    if ( hex.slice(0, 1) == "#" ) hex = hex.slice(1) ;
    if ( hex.length == 3 ) hex = hex.slice(0,1) + hex.slice(0,1) + hex.slice(1,2) + hex.slice(1,2) + hex.slice(2,3) + hex.slice(2,3) ;
    return [ hex.slice( 0, 2 ), hex.slice( 2, 4 ), hex.slice( 4, 6 ) ].map( function ( str ) {
      return parseInt( str, 16 ) ;
    } ) ;
  }
  function setTextColor() {
    function updateColor() {
      const nextColorIndex = currentColorIndex;
      const color = hex2rgb(textColors[nextColorIndex]);
      const root = document.documentElement;
      root.style.setProperty('--apt-color-text-primary', `rgb(${color[0]}, ${color[1]}, ${color[2]})`);
    }
    updateColor();

    // setInterval(updateColor, 5000);
  }

  function setBodyBackgroundColor(duration = 1.6) {
    function updateColor() {
      gsap.to("body", {
        backgroundColor: `#${topRectangleColors[currentColorIndex]}`,
        duration: duration,
        ease: "power1.out",
      });
    }
    updateColor();

    // setInterval(updateColor, 5000);
  }

  // テキストとbody背景は初期値をここで設定している
  setTextColor();
  setBodyBackgroundColor(isInitialized ? 0 : 2);

  // アニメーションループ
  function animate() {
    requestAnimationFrame(animate);

    // 五角形の中央上部の頂点にアニメーションを適用
    animatePentagonVertex(bottomPentagon, 3);
    // 左側の四角形の右上の頂点のみをアニメーション
    animateLeftRectangleVertex(leftRectangle, 2); // 右上の頂点
    // 右側の四角形の左上の頂点のみをアニメーション
    animateRightRectangleVertex(rightRectangle, 3); // 左上の頂点

    renderer.render(scene, camera);
  }

  animate();

  gsap.set("#title-image svg *", {
    fill: "#fff",
  });

  function onLoadingFade() {
    // 各図形の色をアニメーションさせる
    setInterval(() => {
      currentColorIndex = (currentColorIndex + 1) % textColors.length;

      animateColorChange(topRectangle, topRectangleColors);
      animateColorChange(bottomPentagon, bottomPentagonColors);
      animateColorChange(leftRectangle, leftRectangleColors);
      animateColorChange(rightRectangle, rightRectangleColors);

      setTextColor();
      setBodyBackgroundColor();

    }, 5000);

    setTimeout(() => {
      gsap.to("#title-image svg *", {
        duration: 7.5,
        ease: "power4.out",
        fill: "#000",
      });
    }, 2500);
  }
  window.addEventListener('onLoadingFade', onLoadingFade, false);

}
